import {getPathProperties} from '../typings/roleMapping';
import {useModel} from 'umi';
import {SOLUTION_KEYS} from '@/constants/solutions';
import {BackofficeUserModel} from '@/typings/api/user';
import PERMISSIONS from '@/constants/permissions';
import isEmpty from 'lodash/isEmpty';
import FEATURE_FLAGS from '@/constants/featureFlags';
import {PartnerRolloutFlag} from '@/constants/localFeatureFlags';
import {isMimicSession} from '@/services/auth';

// BACKOFFICE_ENV is a build-time define; absent in unit tests.
declare const BACKOFFICE_ENV: string | undefined;

export function checkUserAccessToPath(user: BackofficeUserModel, path: string): boolean {
  const { permissions, featureFlags, rolloutFlags } = getPathProperties(path)
  if (rolloutFlags?.length && !rolloutFlags.some((flag) => hasPartnerRolloutFlag(user, flag)))
    return false

  const hasPermission = permissions?.includes('*') || hasAccess(user, permissions)
  if (!hasPermission) return false

  if (featureFlags?.length > 0)
    return hasFeatureFlag(user, featureFlags)

  return true
}

export const getPermissionsSet = (user: BackofficeUserModel) => {
  if (isEmpty((user))) return new Set()

  if (user.permissionsSet &&
    (user.permissionsSet instanceof Set)) return user.permissionsSet

  const allPermissions = Object.keys((user.Permissions || {})).reduce((acc, key) => {
    return acc.concat(user.Permissions[key]);
  }, []);

  return new Set(allPermissions)
}


// region Access functions

interface IHasAccessOptions {
  every?: boolean
  asterisk?: boolean
}
export const hasAccess = (user: BackofficeUserModel,
                          access: string | string[],
                          options: IHasAccessOptions = {}) => {
  if (!access) return true
  const permissions = getPermissionsSet(user)

  const every = options.every || false
  const asterisk = options.asterisk || false
  // Superuser
  if (asterisk && permissions.has('*')) return true

  if (!Array.isArray(access))
    access = [access]

  access = access.map((permission) => permission.replace('+', ''))

  if (every) return access.every((permission) => permissions.has(permission))
  return access.some((permission) => permissions.has(permission))
}

export const hasFeatureFlag = (user: BackofficeUserModel, flags: string | string[]) => {
  if (isEmpty(flags)) return true
  if (!Array.isArray(flags))
    flags = [flags]

  return flags.some((flag) => hasSingleFeatureFlag(user, flag))
}
export const hasSingleFeatureFlag = (user: BackofficeUserModel, flag: string) => {
  if (!flag) return true
  if (!user || !user.FeatureFlags) return false
  if (!Array.isArray(user.FeatureFlags))
    user.FeatureFlags = Object.keys(user.FeatureFlags).map((flag) => user.FeatureFlags[flag])
  return user.FeatureFlags.includes(flag)
}

/**
 * Marketing attributes — lead source, marketing channel/source/campaign and
 * customer integrations (see MARKETING_DATA_VIEW_FIELDS) — belong to the Lead
 * Management module: the `marketing_data_view` permission is not enough on its
 * own. Gates both the values themselves and their entries in the
 * visibility/column settings of the board, tables and calendar.
 */
export const hasMarketingDataAccess = (user: BackofficeUserModel) =>
  hasFeatureFlag(user, FEATURE_FLAGS.LEAD_MANAGEMENT) &&
  hasAccess(user, [PERMISSIONS.Analytics.MARKETING_DATA_VIEW, PERMISSIONS.BUSINESS_PARTNER])

// `feature` is optional — `hasFeatureFlag` treats an empty one as "no flag
// required" — but destructuring it without a default made TS demand it at every
// call site, so none of them type-checked.
export const CheckAccess = ({access, feature = undefined, every = false, children, fallback = null, asterisk = false}) => {
  const {initialState} = useModel('@@initialState')
  let accessGranted = true
  if (!hasAccess(initialState.currentUser, access, {every, asterisk})) accessGranted = false
  if (!hasFeatureFlag(initialState.currentUser, feature)) accessGranted = false

  return accessGranted ? children : (fallback || null)
}
export const useIsCallTrackingEnabled = () => {
  const {initialState} = useModel('@@initialState')
  return initialState.currentUser?.Partner?.use_call_tracking || false
}
export const useHasCallViewPermission = () => {
  const {initialState} = useModel('@@initialState')
  return hasAccess(initialState.currentUser, [PERMISSIONS.Calls.VIEW,PERMISSIONS.BUSINESS_PARTNER], {asterisk: true})
}

export const hasCallCreatePermission = () => {}
export const hasCallTrackingReportPermission = () => {
  const {initialState} = useModel('@@initialState')
  return hasAccess(initialState.currentUser, [PERMISSIONS.Calls.CALL_CHART_VIEW,PERMISSIONS.BUSINESS_PARTNER], {asterisk: true})
}
// endregion

// region Partner rollout flags

// BACKOFFICE_ENV values whose builds enable every rollout flag.
const ROLLOUT_FLAG_ENVIRONMENTS = ['dev', 'testing', 'devTesting']

export const getCurrentPartnerId = (user: BackofficeUserModel): string | undefined => {
  const currentPartner = user?.Partners?.find((partner) => partner.current_partner)
  return currentPartner?.id || user?.Partner?.id || user?.Partners?.[0]?.id
}

export const hasPartnerRolloutFlag = (user: BackofficeUserModel, flag: PartnerRolloutFlag): boolean => {
  if (typeof BACKOFFICE_ENV !== 'undefined' && ROLLOUT_FLAG_ENVIRONMENTS.includes(BACKOFFICE_ENV)) return true
  if (isMimicSession()) return true
  return flag.partnerIds.includes(getCurrentPartnerId(user))
}

export const usePartnerRolloutFlag = (flag: PartnerRolloutFlag): boolean => {
  const {initialState} = useModel('@@initialState')
  return hasPartnerRolloutFlag(initialState.currentUser, flag)
}

// endregion

// region Solutions

export const getUserSolutions = (user: BackofficeUserModel) => {
  const solutions: string[] = hasFeatureFlag(user, [FEATURE_FLAGS.SCHEDULER, FEATURE_FLAGS.LEAD_MANAGEMENT, FEATURE_FLAGS.AI_AUTOMATION, FEATURE_FLAGS.CALL_TRACKING]) ?  [SOLUTION_KEYS.LEAD_MANAGEMENT] : []
  // The flag is partner-wide, but every page inside needs one of these — without
  // the check an ordinary agent gets the tab with an empty menu behind it.
  if (hasFeatureFlag(user, FEATURE_FLAGS.LEAD_GENERATION) &&
    hasAccess(user, [PERMISSIONS.BUSINESS_PARTNER, PERMISSIONS.Analytics.VIEW]))
    solutions.push(SOLUTION_KEYS.LEAD_GENERATION)

  return solutions
}

export const getDefaultSolution = (user: BackofficeUserModel) => {
  const solutions = getUserSolutions(user)
  return solutions[0]
}

export const getSolutionFromPath = (path: string): string | undefined => {
  const { featureFlags } = getPathProperties(path);
  if (!featureFlags || featureFlags.length === 0) return undefined;

  if (featureFlags.includes(FEATURE_FLAGS.LEAD_GENERATION)) {
    return SOLUTION_KEYS.LEAD_GENERATION;
  }

  const leadManagementFlags = [
    FEATURE_FLAGS.SCHEDULER,
    FEATURE_FLAGS.LEAD_MANAGEMENT,
    FEATURE_FLAGS.AI_AUTOMATION,
    FEATURE_FLAGS.CALL_TRACKING,
  ];

  if (featureFlags.some(flag => leadManagementFlags.includes(flag))) {
    return SOLUTION_KEYS.LEAD_MANAGEMENT;
  }

  return undefined;
}

// endregion
